<!-- ========== 新加坡进销存系统 - 档口前端 ========== -->
<!-- src/views/Stall/Dashboard.vue -->
<template>
  <div class="stall-dashboard">
    <div class="page-header">
      <div class="header-content">
        <h1>{{ stallName }} - {{ $t('dashboard.title') }}</h1>
        <p class="subtitle">{{ $t('dashboard.subtitle') }}{{ $t('product.stock') }}、销售、采购数据展示</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="showQuickAdd">
          <i class="icon-plus"></i> {{ $t('dashboard.quickAdd') }}
        </button>
        <button class="btn btn-secondary" @click="exportData">
          <i class="icon-download"></i> {{ $t('dashboard.exportData') }}
        </button>
      </div>
    </div>

    <!-- 数据概览卡片 -->
    <div class="overview-grid">
      <div class="overview-card">
        <div class="card-header">
          <span class="card-title">{{ $t('dashboard.todaySales') }}</span>
          <i class="icon-trending-up"></i>
        </div>
        <div class="card-value">¥{{ todaySales }}</div>
        <div class="card-footer">
          <span class="trend up">↑ 12.5%</span>
          <span class="compared">{{ $t('dashboard.todayGrowth') }}</span>
        </div>
      </div>

      <div class="overview-card">
        <div class="card-header">
          <span class="card-title">{{ $t('dashboard.salesCount') }}</span>
          <i class="icon-shopping-bag"></i>
        </div>
        <div class="card-value">{{ todayOrderCount }}</div>
        <div class="card-footer">
          <span class="trend up">↑ 8 {{ $t('common.quantity') }}</span>
          <span class="compared">{{ $t('dashboard.todayGrowth') }}</span>
        </div>
      </div>

      <div class="overview-card">
        <div class="card-header">
          <span class="card-title">{{ $t('product.stock') }}{{ $t('dashboard.totalStockValue') }}</span>
          <i class="icon-package"></i>
        </div>
        <div class="card-value">¥{{ totalInventoryValue }}</div>
        <div class="card-footer">
          <span class="count">{{ totalInventoryItems }} {{ $t('dashboard.items') }}</span>
          <span class="compared">{{ $t('dashboard.crossCategories') }} {{ totalCategories }} {{ $t('dashboard.categories') }}</span>
        </div>
      </div>

      <div class="overview-card">
        <div class="card-header">
          <span class="card-title">低{{ $t('product.stock') }}{{ $t('dashboard.lowStockAlert') }}</span>
          <i class="icon-alert-circle"></i>
        </div>
        <div class="card-value warning">{{ lowStockCount }}</div>
        <div class="card-footer">
          <span class="action-link" @click="showLowStock">查看{{ $t('common.detail') }}</span>
          <span class="compared">{{ $t('dashboard.needRestock') }}</span>
        </div>
      </div>
    </div>

    <!-- 主要功能区 -->
    <div class="main-content">
      <!-- 左侧：快速操作和图表 -->
      <div class="content-left">
        <!-- 快速操作菜单 -->
        <div class="quick-actions">
          <div class="actions-title">{{ $t('dashboard.quickActions') }}{{ $t('common.action') }}</div>
          <div class="actions-grid">
            <div class="action-item" @click="navigateTo('sales')">
              <div class="action-icon sales">
                <i class="icon-shopping-cart"></i>
              </div>
              <div class="action-text">
                <div class="action-name">{{ $t('Stall.Dashboard.line84_text') }}</div>
                <div class="action-desc">{{ $t('Stall.Dashboard.line85_text') }}</div>
              </div>
            </div>

            <div class="action-item" @click="navigateTo('purchase')">
              <div class="action-icon purchase">
                <i class="icon-inbox"></i>
              </div>
              <div class="action-text">
                <div class="action-name">{{ $t('stall.purchase') }}</div>
                <div class="action-desc">{{ $t('Stall.Dashboard.line95_text') }}</div>
              </div>
            </div>

            <div class="action-item" @click="navigateTo('inventory')">
              <div class="action-icon inventory">
                <i class="icon-layers"></i>
              </div>
              <div class="action-text">
                <div class="action-name">{{ $t('stall.inventory') }}</div>
                <div class="action-desc">查看{{ $t('product.stock') }}信息</div>
              </div>
            </div>

            <div class="action-item" @click="navigateTo('return')">
              <div class="action-icon return">
                <i class="icon-undo"></i>
              </div>
              <div class="action-text">
                <div class="action-name">{{ $t('stall.return') }}</div>
                <div class="action-desc">{{ $t('Stall.Dashboard.line115_text') }}</div>
              </div>
            </div>

            <div class="action-item" @click="navigateTo('report')">
              <div class="action-icon report">
                <i class="icon-bar-chart"></i>
              </div>
              <div class="action-text">
                <div class="action-name">{{ $t('Stall.Dashboard.line124_text') }}</div>
                <div class="action-desc">{{ $t('Stall.Dashboard.line125_text') }}</div>
              </div>
            </div>

            <div class="action-item" @click="navigateTo('settings')">
              <div class="action-icon settings">
                <i class="icon-settings"></i>
              </div>
              <div class="action-text">
                <div class="action-name">{{ $t('Stall.Dashboard.line134_text') }}</div>
                <div class="action-desc">{{ $t('navigation.products') }}和{{ $t('product.category') }}管理</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 销售趋势图表 -->
        <div class="chart-container">
          <div class="chart-header">
            <h3>{{ $t('Stall.Dashboard.line144_text') }}</h3>
            <div class="chart-controls">
              <button 
                v-for="period in ['day', 'week', 'month']" 
                :key="period"
                :class="['btn-period', { active: selectedPeriod === period }]"
                @click="selectedPeriod = period"
              >
                {{ periodLabels[period] }}
              </button>
            </div>
          </div>
          <div class="chart" ref="salesChart"></div>
        </div>
      </div>

      <!-- 右侧：最近交易和库存预警 -->
      <div class="content-right">
        <!-- 最近销售交易 -->
        <div class="recent-section">
          <div class="section-header">
            <h3>{{ $t('dashboard.recentSales') }}</h3>
            <router-link to="/stall/sales" class="view-all">{{ $t('stall.viewAll') }}</router-link>
          </div>
          <div class="recent-list">
            <div v-if="recentSales.length === 0" class="empty-state">
              <p>{{ $t('dashboard.noSales') }}</p>
            </div>
            <div 
              v-for="sale in recentSales.slice(0, 5)" 
              :key="sale.id" 
              class="recent-item"
            >
              <div class="item-info">
                <div class="item-title">{{ sale.productName }}</div>
                <div class="item-time">{{ formatTime(sale.createdAt) }}</div>
              </div>
              <div class="item-amount">
                <div class="amount">¥{{ sale.totalAmount }}</div>
                <div class="quantity">x{{ sale.quantity }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 库存预警 -->
        <div class="recent-section">
          <div class="section-header">
            <h3>{{ $t('product.stock') }}预警</h3>
            <span class="warning-count">{{ lowStockCount }}</span>
          </div>
          <div class="recent-list">
            <div v-if="lowStockItems.length === 0" class="empty-state">
              <p>{{ $t('product.stock') }}{{ $t('dashboard.stockSufficient') }}</p>
            </div>
            <div 
              v-for="item in lowStockItems.slice(0, 5)" 
              :key="item.id" 
              class="recent-item warning"
            >
              <div class="item-info">
                <div class="item-title">{{ item.productName }}</div>
                <div class="item-code">{{ item.productCode }}</div>
              </div>
              <div class="item-stock">
                <div class="stock-value">{{ item.currentStock }}</div>
                <div class="stock-unit">{{ item.unit }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 快速采购 -->
        <div class="recent-section">
          <div class="section-header">
            <h3>{{ $t('dashboard.quickPurchase') }}</h3>
            <button class="btn-small" @click="showPurchaseModal">+ {{ $t('common.create') }}</button>
          </div>
          <div class="quick-purchase">
            <div class="form-group">
              <label>{{ $t('dashboard.selectProduct') }}{{ $t('navigation.products') }}</label>
              <select v-model="quickPurchase.productId" class="form-control">
                <option value="">{{ $t('common.select') }}{{ $t('navigation.products') }}</option>
                <option 
                  v-for="product in products" 
                  :key="product.id" 
                  :value="product.id"
                >
                  {{ product.name }} - ¥{{ product.purchasePrice }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ $t('dashboard.purchaseQuantity') }}{{ $t('common.quantity') }}</label>
              <input 
                v-model.number="quickPurchase.quantity" 
                type="number" 
                min="1" 
                class="form-control" 
                :placeholder="$t('dashboard.inputQuantity')"
              />
            </div>
            <button class="btn btn-primary btn-block" @click="submitQuickPurchase">
              {{ $t('dashboard.submitPurchase') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import * as echarts from 'echarts';

export default {
  name: 'StallDashboard',
  data() {
    return {
      stallName: '新加坡进销存系统',
      selectedPeriod: 'day',
      periodLabel: {
        day: '',
        week: '', 
        month: ''
      },
      recentSales: [],
      lowStockItems: [],
      products: [],
      quickPurchase: {
        productId: '',
        quantity: 1
      },
      chartInstance: null,
      todaySales: '0.00',
      todayOrderCount: 0,
      totalInventoryValue: '0.00',
      totalInventoryItems: 0,
      totalCategories: 0,
      lowStockCount: 0
    };
  },
  computed: {
    ...mapGetters('stall', ['getStallInfo', 'getDashboardData']),
    periodLabels() {
      return {
        day: this.$t('dashboard.today'),
        week: this.$t('dashboard.thisWeek'),
        month: this.$t('dashboard.thisMonth')
      };
    }
  },
  methods: {
    ...mapActions('stall', [
      'getStallDashboard',
      'getRecentSales', 
      'getLowStockItems',
      'getProducts',
      'createPurchaseOrder'
    ]),
    
    async loadDashboardData() {
      try {
        const data = await this.getStallDashboard();
        this.todaySales = data.todaySales.toFixed(2);
        this.todayOrderCount = data.todayOrderCount;
        this.totalInventoryValue = data.totalInventoryValue.toFixed(2);
        this.totalInventoryItems = data.totalInventoryItems;
        this.totalCategories = data.totalCategories;
        this.lowStockCount = data.lowStockCount;
        
        await this.loadRecentSales();
        await this.loadLowStockItems();
        await this.loadProducts();
        this.initChart();
      } catch (error) {
        this.$message.error(this.$t('dashboard.loadFailed'));
      }
    },
    
    async loadRecentSales() {
      try {
        this.recentSales = await this.getRecentSales({ limit: 10 });
      } catch (error) {
        console.error('加载最近销售失败', error);
      }
    },
    
    async loadLowStockItems() {
      try {
        this.lowStockItems = await this.getLowStockItems();
      } catch (error) {
        console.error('加载低库存商品失败', error);
      }
    },
    
    async loadProducts() {
      try {
        this.products = await this.getProducts();
      } catch (error) {
        console.error('加载商品列表失败', error);
      }
    },
    
    initChart() {
      if (!this.$refs.salesChart) return;
      
      const chartData = this.generateChartData();
      this.chartInstance = echarts.init(this.$refs.salesChart);
      
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          textStyle: { color: '#fff' }
        },
        grid: {
          left: 60,
          right: 20,
          top: 20,
          bottom: 40,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: chartData.dates,
          boundaryGap: false,
          axisLine: { lineStyle: { color: '#ddd' } },
          axisLabel: { fontSize: 12, color: '#666' }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#ddd' } },
          axisLabel: { fontSize: 12, color: '#666' },
          splitLine: { lineStyle: { color: '#f0f0f0' } }
        },
        series: [{
          data: chartData.sales,
          type: 'line',
          smooth: true,
          stroke: 'none',
          fill: 'gradient',
          itemStyle: { color: '#3498db' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(52, 152, 219, 0.3)' },
              { offset: 1, color: 'rgba(52, 152, 219, 0.1)' }
            ])
          }
        }]
      };
      
      this.chartInstance.setOption(option);
      window.addEventListener('resize', () => this.chartInstance?.resize());
    },
    
    generateChartData() {
      // 根据选择的时期生成图表数据
      // const today = new Date(); // eslint-disable-line no-unused-vars
      let dates = [];
      let sales = [];
      
      if (this.selectedPeriod === 'day') {
        // 按小时显示
        for (let i = 0; i < 24; i++) {
          dates.push(`${i}:00`);
          sales.push(Math.floor(Math.random() * 1000) + 100);
        }
      } else if (this.selectedPeriod === 'week') {
        // 按天显示
        const days = [this.$t('dashboard.mon'), this.$t('dashboard.tue'), this.$t('dashboard.wed'), this.$t('dashboard.thu'), this.$t('dashboard.fri'), this.$t('dashboard.sat'), this.$t('dashboard.sun')];
        dates = days;
        sales = days.map(() => Math.floor(Math.random() * 5000) + 1000);
      } else {
        // 按周显示
        for (let i = 0; i < 4; i++) {
          dates.push(`${this.$t('dashboard.week')}${i + 1}${this.$t('dashboard.weekSuffix')}`);
          sales.push(Math.floor(Math.random() * 30000) + 10000);
        }
      }
      
      return { dates, sales };
    },
    
    async submitQuickPurchase() {
      if (!this.quickPurchase.productId) {
        this.$message.warning(this.$t('dashboard.selectProductWarning'));
        return;
      }
      if (this.quickPurchase.quantity <= 0) {
        this.$message.warning(this.$t('dashboard.invalidQuantity'));
        return;
      }
      
      try {
        await this.createPurchaseOrder({
          productId: this.quickPurchase.productId,
          quantity: this.quickPurchase.quantity
        });
        this.$message.success(this.$t('dashboard.purchaseCreated'));
        this.quickPurchase = { productId: '', quantity: 1 };
        this.loadDashboardData();
      } catch (error) {
        this.$message.error(this.$t('dashboard.purchaseFailed'));
      }
    },
    
    navigateTo(route) {
      this.$router.push(`/stall/${route}`);
    },
    
    showLowStock() {
      this.$router.push('/stall/inventory?tab=lowstock');
    },
    
    showQuickAdd() {
      // 显示快速录入对话框
      this.$refs.quickAddModal?.open();
    },
    
    showPurchaseModal() {
      // 显示采购对话框
    },
    
    exportData() {
      // 导出数据
      this.$message.success(this.$t('dashboard.exporting'));
    },
    
    formatTime(time) {
      const date = new Date(time);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
  },
  
  watch: {
    selectedPeriod() {
      this.initChart();
    }
  },
  
  mounted() {
    this.loadDashboardData();
  },
  
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  }
};
</script>

<style scoped lang="scss">
.stall-dashboard {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    
    .header-content {
      h1 {
        font-size: 28px;
        font-weight: bold;
        color: #2c3e50;
        margin: 0 0 8px 0;
      }
      .subtitle {
        color: #999;
        font-size: 13px;
        margin: 0;
      }
    }
    
    .header-actions {
      display: flex;
      gap: 12px;
    }
  }

  // 数据概览卡片
  .overview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 30px;
    
    .overview-card {
      background: white;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
      
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        
        .card-title {
          font-size: 13px;
          color: #999;
          font-weight: 500;
        }
        
        i {
          font-size: 24px;
          color: #3498db;
        }
      }
      
      .card-value {
        font-size: 28px;
        font-weight: bold;
        color: #2c3e50;
        margin-bottom: 12px;
        
        &.warning {
          color: #e74c3c;
        }
      }
      
      .card-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
        color: #999;
        
        .trend {
          &.up { color: #2ecc71; }
          &.down { color: #e74c3c; }
        }
        
        .compared {
          font-size: 11px;
        }
        
        .action-link {
          color: #3498db;
          cursor: pointer;
          
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }

  // 主要内容区
  .main-content {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 20px;
    
    @media (max-width: 1400px) {
      grid-template-columns: 1fr;
    }
    
    .content-left {
      display: flex;
      flex-direction: column;
      gap: 20px;
      
      .quick-actions {
        background: white;
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        
        .actions-title {
          font-size: 14px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 16px;
        }
        
        .actions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
          
          .action-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.3s ease;
            background-color: #f9f9f9;
            
            &:hover {
              background-color: #f0f0f0;
            }
            
            .action-icon {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 40px;
              height: 40px;
              border-radius: 6px;
              font-size: 20px;
              color: white;
              
              &.sales { background-color: #3498db; }
              &.purchase { background-color: #2ecc71; }
              &.inventory { background-color: #f39c12; }
              &.return { background-color: #9b59b6; }
              &.report { background-color: #e74c3c; }
              &.settings { background-color: #34495e; }
            }
            
            .action-text {
              flex: 1;
              
              .action-name {
                font-size: 13px;
                font-weight: 600;
                color: #2c3e50;
                margin-bottom: 2px;
              }
              
              .action-desc {
                font-size: 11px;
                color: #999;
              }
            }
          }
        }
      }
      
      .chart-container {
        background: white;
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        
        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          
          h3 {
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            color: #2c3e50;
          }
          
          .chart-controls {
            display: flex;
            gap: 8px;
            
            .btn-period {
              padding: 6px 12px;
              border: 1px solid #ddd;
              background: white;
              border-radius: 4px;
              font-size: 12px;
              cursor: pointer;
              transition: all 0.3s ease;
              color: #666;
              
              &:hover {
                border-color: #3498db;
                color: #3498db;
              }
              
              &.active {
                background-color: #3498db;
                color: white;
                border-color: #3498db;
              }
            }
          }
        }
        
        .chart {
          height: 300px;
        }
      }
    }
    
    .content-right {
      display: flex;
      flex-direction: column;
      gap: 20px;
      
      .recent-section {
        background: white;
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #eee;
          
          h3 {
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            color: #2c3e50;
          }
          
          .view-all,
          .btn-small {
            font-size: 12px;
            color: #3498db;
            cursor: pointer;
            
            &:hover {
              text-decoration: underline;
            }
          }
          
          .warning-count {
            display: inline-block;
            min-width: 24px;
            height: 24px;
            line-height: 24px;
            text-align: center;
            background-color: #e74c3c;
            color: white;
            border-radius: 12px;
            font-size: 12px;
            font-weight: bold;
          }
        }
        
        .recent-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          
          .empty-state {
            text-align: center;
            padding: 30px 0;
            color: #999;
            font-size: 13px;
          }
          
          .recent-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px;
            border-radius: 6px;
            background-color: #f9f9f9;
            transition: all 0.3s ease;
            
            &:hover {
              background-color: #f0f0f0;
            }
            
            &.warning {
              background-color: #fff5f5;
              border-left: 3px solid #e74c3c;
            }
            
            .item-info {
              flex: 1;
              
              .item-title {
                font-size: 12px;
                font-weight: 500;
                color: #2c3e50;
                margin-bottom: 4px;
              }
              
              .item-time,
              .item-code {
                font-size: 11px;
                color: #999;
              }
            }
            
            .item-amount,
            .item-stock {
              text-align: right;
              
              .amount,
              .stock-value {
                font-size: 13px;
                font-weight: 600;
                
                &.stock-value {
                  color: #e74c3c;
                }
              }
              
              .quantity,
              .stock-unit {
                font-size: 11px;
                color: #999;
              }
            }
          }
        }
        
        .quick-purchase {
          display: flex;
          flex-direction: column;
          gap: 12px;
          
          .form-group {
            display: flex;
            flex-direction: column;
            
            label {
              font-size: 12px;
              font-weight: 500;
              color: #666;
              margin-bottom: 6px;
            }
            
            .form-control {
              padding: 8px 10px;
              border: 1px solid #ddd;
              border-radius: 4px;
              font-size: 12px;
              font-family: inherit;
              
              &:focus {
                outline: none;
                border-color: #3498db;
                box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
              }
            }
          }
          
          .btn-block {
            width: 100%;
          }
        }
      }
    }
  }
}
</style>